import { getPublishErrorDescription, PUBLISH_STATUS_KEY } from '@/hooks/queries/useBcpPublishStatus'
import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { LAUNCH_UPDATE_ERROR_TYPES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const launchesSearchSchema = z.object({
  launchName: z.string().optional(),
  erp: z.array(z.string()).optional(),
  material: z.array(z.string()).optional(),
  active: z.boolean().optional(),
  ...datatableSearchSchema.shape,
})

export type LaunchesSearch = z.infer<typeof launchesSearchSchema>

export const launchDetailsSchema = z.object({
  launchName: z.string().min(3, {
    message: 'A launch name must be at least 3 characters long.',
  }),
  launchDescr: z.string(),
})

type LaunchDetailsBody = z.infer<typeof launchDetailsSchema>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const launchMaterialsSchema = z.array(
  z.object({
    launchMaterialId: z.number(),
    material: z.string().nullable(),
    isOrder: z.boolean().nullable(),
    isFeatured: z.boolean().nullable(),
    isNewProduct: z.boolean().nullable(),
    isOutOfStock: z.boolean().nullable(),
    isPcapture: z.boolean().nullable(),
    isSellin: z.boolean().nullable(),
    isStrategic: z.boolean().nullable(),
    sequence: z.string().nullable().optional(),
  }),
)

type LaunchMaterialsBody = z.infer<typeof launchMaterialsSchema>

type UseCreateLaunchArgs = {
  queries: LaunchesSearch
  handleOnSuccess: (launchId: number) => void
}

type UseUpdateLaunchDetailsArgs = QueryHandlers & {
  launchId: number
  details: LaunchDetailsBody
  materials: LaunchMaterialsBody
  newErps: string[]
  isActive: boolean
}

type UseDeleteLaunchesArgs = QueryHandlers & {
  launchIds: number[]
  queries: LaunchesSearch
}

type UseDeleteLaunchErpsArgs = QueryHandlers & {
  launchId: number
}

type UseDeleteInactiveLaunchErpsArgs = QueryHandlers & {
  launchId: number
}

type UseDeleteLaunchMaterialsArgs = QueryHandlers & {
  launchId: number
}

async function getLaunches(search: LaunchesSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.launches.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const launches = await res.json()
  return launches
}

async function createLaunch(launchDetails: LaunchDetailsBody) {
  const api = getApi()

  const res = await api.assortment.launches.$post({
    json: launchDetails,
  })

  const launch = await res.json()
  return launch
}

async function deleteLaunches(launchIds: number[]) {
  const api = getApi()

  await api.assortment.launches.$delete({
    json: launchIds,
  })
}

async function publishLaunches() {
  const api = getApi()
  const res = await api.assortment.launches.publish.$post()
  return res.json()
}

async function getLaunchDetails(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.launches[':id'].$get({ param: { id } }, { init: { signal } })

  const launchDetails = await res.json()
  return launchDetails
}

async function updateLaunch(
  launchId: number,
  details: LaunchDetailsBody,
  materials: LaunchMaterialsBody,
  newErps: string[],
  isActive: boolean,
) {
  const api = getApi()

  await api.assortment.launches[':id'].$patch({
    param: { id: String(launchId) },
    json: { details, materials, newErps, isActive },
  })
}

export async function deleteLaunchErps(launchId: number, sapErps: string[]) {
  const api = getApi()

  await api.assortment.launches[':id'].erps.$delete({
    param: { id: String(launchId) },
    json: sapErps,
  })
}

export async function deleteInactiveLaunchErps(launchId: number) {
  const api = getApi()

  await api.assortment.launches[':id'].erps.inactive.$delete({
    param: { id: String(launchId) },
  })
}

export async function deleteLaunchMaterials(launchId: number, launchMaterialIds: number[]) {
  const api = getApi()

  await api.assortment.launches[':id'].materials.$delete({
    param: { id: String(launchId) },
    json: launchMaterialIds,
  })
}

export async function cleanUpObsoleteLaunchMaterials() {
  const api = getApi()
  const res = await api.assortment.launches.materials.$delete()

  const message = await res.json()
  return message
}

async function getLaunchErpsCsv(launchId: number, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(`/assortment/launches/${launchId}/erps/export`, signal)

  return csvExport
}

async function getLaunchMaterialsCsv(launchId: number, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(
    `/assortment/launches/${launchId}/materials/export`,
    signal,
  )

  return csvExport
}

async function getLaunchesCsv(search: LaunchesSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(`/assortment/launches/export?${queryParams}`, signal)

  return csvExport
}

export async function checkLaunchValidation(
  launchId: number,
  sapErps: string[],
  materials: string[],
) {
  const api = getApi()
  const res = await api.assortment.launches[':id'].validation.$post({
    param: { id: String(launchId) },
    json: { sapErps, materials },
  })

  const validation = await res.json()
  return validation
}

async function cleanInactiveErpsInLaunches() {
  const api = getApi()
  const res = await api.assortment.launches.erps.inactive.$delete()

  const message = await res.json()
  return message
}

export function useLaunches(queries: LaunchesSearch) {
  return useQuery({
    queryKey: ['launches', queries],
    queryFn: ({ signal }) => getLaunches(queries, signal),
  })
}

export function useCreateLaunch({ queries, handleOnSuccess }: UseCreateLaunchArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['launches', queries],
    mutationFn: createLaunch,
    onSuccess: ({ launch }) => {
      handleOnSuccess(launch.launchId)

      queryClient.invalidateQueries({
        queryKey: ['launches', queries],
      })

      toast.success('Success', {
        description: `Launch created successfully.`,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong. Launch creation failed.',
        className: toastClassName,
      })
    },
  })
}

export function useDeleteLaunches({ launchIds, queries, handleOnSuccess }: UseDeleteLaunchesArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteLaunches'],
    mutationFn: () => deleteLaunches(launchIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['launches', queries],
      })

      toast.success('Success', {
        description: `Launches have been deleted successfully.`,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong. Delete failed.',
        className: toastClassName,
      })
    },
  })
}

export function usePublishLaunches() {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['publishLaunches'],
    mutationFn: () => publishLaunches(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PUBLISH_STATUS_KEY, 'launches'],
      })

      toast.info(t('toast.info'), {
        description: t('publish.initiated', { publishType: t('publish.type.launches') }),
      })
    },
    onError: (error) => {
      const description = getPublishErrorDescription(error, t, t('publish.type.launches'))
      toast.error(t('toast.error'), {
        description,
      })
    },
  })
}

export const launchDetailsQueryOptions = (id: string) => ({
  queryKey: ['launchDetails', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getLaunchDetails(id, signal),
})

export function useLaunchDetails(id: string) {
  return useQuery(launchDetailsQueryOptions(id))
}

export function useUpdateLaunch({
  launchId,
  details,
  materials,
  newErps,
  isActive,
  handleOnSuccess,
}: UseUpdateLaunchDetailsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('launches')

  return useMutation({
    mutationKey: ['updateLaunch', launchId],
    mutationFn: () => updateLaunch(launchId, details, materials, newErps, isActive),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['launchDetails', String(launchId)],
      })

      toast.success('Success', {
        description: `Launch saved!`,
        className: toastClassName,
      })
    },
    onError: (error) => {
      const issues = extractZodIssues(error)
      const isLaunchNameConflict = issues?.some(
        (issue) =>
          issue.code === 'custom' &&
          (issue.params as { type?: string } | undefined)?.type ===
            LAUNCH_UPDATE_ERROR_TYPES.launchNameConflict,
      )

      toast.error('Error', {
        description: isLaunchNameConflict
          ? t('launchDetails.errors.launchNameConflict')
          : 'Something went wrong. Your changes have not been saved.',
        className: toastClassName,
      })
    },
  })
}

export function useDeleteLaunchErps({ launchId, handleOnSuccess }: UseDeleteLaunchErpsArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteLaunchErps', launchId],
    mutationFn: (sapErps: string[]) => deleteLaunchErps(launchId, sapErps),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['launchDetails', String(launchId)],
      })

      toast.success('Success', {
        description: `ERPs deleted successfully.`,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong. Delete failed.',
        className: toastClassName,
      })
    },
  })
}

export function useDeleteInactiveLaunchErps({ launchId }: UseDeleteInactiveLaunchErpsArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteInactiveLaunchErps', launchId],
    mutationFn: () => deleteInactiveLaunchErps(launchId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['launchDetails', String(launchId)],
      })

      toast.success('Success', {
        description: `Inactive ERPs deleted successfully.`,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong. Delete failed.',
        className: toastClassName,
      })
    },
  })
}

export function useDeleteLaunchMaterials({
  launchId,
  handleOnSuccess,
}: UseDeleteLaunchMaterialsArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteLaunchMaterials', launchId],
    mutationFn: (materialIds: number[]) => deleteLaunchMaterials(launchId, materialIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['launchDetails', String(launchId)],
      })

      toast.success('Success', {
        description: `Materials deleted successfully.`,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong. Delete failed.',
        className: toastClassName,
      })
    },
  })
}

export function useCleanUpObsoleteLaunchMaterials(queries: LaunchesSearch) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['cleanUpObsoleteLaunchMaterials'],
    mutationFn: cleanUpObsoleteLaunchMaterials,
    onSuccess: ({ message }) => {
      queryClient.invalidateQueries({
        queryKey: ['launches', queries],
      })

      toast.success('Success', {
        description: message,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong while cleaning up launch materials.',
        className: toastClassName,
      })
    },
  })
}

export function useCleanInactiveErpsInLaunches(queries: LaunchesSearch) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['cleanInactiveLaunchErps'],
    mutationFn: cleanInactiveErpsInLaunches,
    onSuccess: ({ message }) => {
      queryClient.invalidateQueries({
        queryKey: ['launches', queries],
      })

      toast.success('Success', {
        description: message,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error('Error', {
        description: 'Something went wrong while cleaning up inactive ERPs.',
        className: toastClassName,
      })
    },
  })
}

export const launchErpsExportQueryOptions = (launchId: number) => ({
  queryKey: ['launchErpsExport', launchId],
  queryFn: ({ signal }: { signal: AbortSignal }) => getLaunchErpsCsv(launchId, signal),
})

export const launchMaterialsExportQueryOptions = (launchId: number) => ({
  queryKey: ['launchMaterialsExport', launchId],
  queryFn: ({ signal }: { signal: AbortSignal }) => getLaunchMaterialsCsv(launchId, signal),
})

export const launchesExportQueryOptions = (queries: LaunchesSearch) => ({
  queryKey: ['launchErpsExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getLaunchesCsv(queries, signal),
})
